import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number | null} */
  #notificationTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    if (this.#notificationTimeout) clearTimeout(this.#notificationTimeout);
  }

  /** @param {CartAddEvent} event */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open') && !event?.detail?.data?.suppressDrawer) {
      this.showDialog();
      const addedItem = /** @type {{ addedItem?: { title: string, image: string, price: number, url: string } }} */ (event?.detail?.data ?? {}).addedItem;
      if (addedItem) {
        this.#showAddedNotification(addedItem);
      }
    }
  };

  /**
   * Shows a friendly "Added to your cart!" notification popup inside the cart drawer.
   * @param {{ title: string, image: string, price: number, url: string }} item
   */
  #showAddedNotification(item) {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Remove any existing notification
    const existing = dialog.querySelector('.cart-added-notification');
    if (existing) existing.remove();
    if (this.#notificationTimeout) clearTimeout(this.#notificationTimeout);

    const price = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format((item.price || 0) / 100);

    // Build notification DOM safely (no innerHTML with user data)
    const notification = document.createElement('div');
    notification.className = 'cart-added-notification';

    const overlay = document.createElement('div');
    overlay.className = 'cart-added-notification__overlay';

    const popup = document.createElement('div');
    popup.className = 'cart-added-notification__popup';

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'cart-added-notification__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.type = 'button';
    closeBtn.textContent = '\u00D7';

    // Success banner
    const banner = document.createElement('div');
    banner.className = 'cart-added-notification__banner';
    const checkSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    checkSvg.setAttribute('viewBox', '0 0 24 24');
    checkSvg.setAttribute('width', '22');
    checkSvg.setAttribute('height', '22');
    checkSvg.setAttribute('aria-hidden', 'true');
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '12');
    circle.setAttribute('fill', '#4CAF50');
    const checkPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    checkPath.setAttribute('d', 'M9 12.5l2 2 4-4');
    checkPath.setAttribute('stroke', '#fff');
    checkPath.setAttribute('stroke-width', '2');
    checkPath.setAttribute('fill', 'none');
    checkPath.setAttribute('stroke-linecap', 'round');
    checkPath.setAttribute('stroke-linejoin', 'round');
    checkSvg.appendChild(circle);
    checkSvg.appendChild(checkPath);
    const bannerText = document.createElement('span');
    bannerText.textContent = 'Added to your cart!';
    banner.appendChild(checkSvg);
    banner.appendChild(bannerText);

    // Product row
    const productRow = document.createElement('div');
    productRow.className = 'cart-added-notification__product';

    if (item.image) {
      const img = document.createElement('img');
      img.className = 'cart-added-notification__image';
      const separator = item.image.includes('?') ? '&' : '?';
      img.src = item.image + separator + 'width=160';
      img.alt = item.title || '';
      img.width = 80;
      img.height = 80;
      img.loading = 'eager';
      productRow.appendChild(img);
    } else {
      productRow.classList.add('cart-added-notification__product--no-image');
    }

    const details = document.createElement('div');
    details.className = 'cart-added-notification__details';
    const titleEl = document.createElement('h3');
    titleEl.className = 'cart-added-notification__title';
    titleEl.textContent = item.title || '';
    const priceEl = document.createElement('p');
    priceEl.className = 'cart-added-notification__price';
    priceEl.textContent = price;
    details.appendChild(titleEl);
    details.appendChild(priceEl);
    productRow.appendChild(details);

    // Action buttons
    const actions = document.createElement('form');
    actions.className = 'cart-added-notification__actions';
    actions.action = '/cart';
    actions.method = 'post';

    const viewCartLink = document.createElement('a');
    viewCartLink.className = 'cart-added-notification__view-cart button button-secondary';
    viewCartLink.textContent = 'View cart';
    viewCartLink.href = '/cart';

    const checkoutButton = document.createElement('button');
    checkoutButton.className = 'cart-added-notification__checkout button';
    checkoutButton.textContent = 'Checkout';
    checkoutButton.type = 'submit';
    checkoutButton.name = 'checkout';

    actions.appendChild(viewCartLink);
    actions.appendChild(checkoutButton);

    popup.appendChild(closeBtn);
    popup.appendChild(banner);
    popup.appendChild(productRow);
    popup.appendChild(actions);

    notification.appendChild(overlay);
    notification.appendChild(popup);
    dialog.appendChild(notification);

    // Animate in
    requestAnimationFrame(() => {
      notification.classList.add('cart-added-notification--visible');
    });

    const closeNotification = () => {
      notification.classList.remove('cart-added-notification--visible');
      notification.classList.add('cart-added-notification--closing');
      setTimeout(() => notification.remove(), 300);
      if (this.#notificationTimeout) clearTimeout(this.#notificationTimeout);
    };

    viewCartLink.addEventListener('click', closeNotification);
    closeBtn.addEventListener('click', closeNotification);
    overlay.addEventListener('click', closeNotification);

    this.#notificationTimeout = setTimeout(closeNotification, 8000);
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
